import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { 
  ArrowLeft, 
  Calendar, 
  Building2, 
  User, 
  AlertTriangle,
  Trash2,
  Edit,
  Printer,
  Image as ImageIcon,
  Check,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { IncidentValues } from "@/components/incidents/IncidentValues";
import { notifyIncidentChange } from "@/utils/incidentEvents";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    fetchIncident();
  }, [id]);

  const fetchIncident = async () => {
    try {
      const response = await axios.get(`${API}/incidents/${id}`);
      setIncident(response.data);
      
      // Load images
      if (!response.data.form_snapshot?.fields?.length && response.data.bilder && response.data.bilder.length > 0) {
        loadImages(response.data.bilder);
      }
    } catch (error) {
      console.error("Error fetching incident:", error);
      toast.error("Unfall nicht gefunden");
      navigate("/unfaelle");
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async (paths) => {
    const urls = {};
    for (const path of paths) {
      try {
        const response = await axios.get(`${API}/files/${path}`, {
          responseType: 'blob'
        });
        urls[path] = URL.createObjectURL(response.data);
      } catch (error) {
        console.error(`Error loading image ${path}:`, error);
      }
    }
    setImageUrls(urls);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/incidents/${id}`);
      notifyIncidentChange();
      toast.success("Unfallmeldung gelöscht");
      navigate("/unfaelle");
    } catch (error) {
      console.error("Error deleting incident:", error);
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd. MMMM yyyy", { locale: de });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!incident) {
    return null;
  }

  return (
    <div className="max-w-4xl animate-fade-in" data-testid="incident-detail-page">
      {/* Back Link */}
      <Link 
        to="/unfaelle"
        data-testid="back-to-list"
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-50 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zur Übersicht
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="min-w-0 md:flex-1">
          <p className="text-xs font-bold uppercase text-yellow-500 mb-2">Meldungsdetails</p>
          <h1 data-testid="incident-detail-title" className="text-3xl md:text-4xl font-black text-zinc-50 mb-2 break-words" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {incident.meldungsart || "Meldung"}
          </h1>
          <div className="flex items-center gap-4 text-zinc-400">
            <div data-testid="incident-created-date" className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4" />
              Erfasst am {formatDate(incident.created_at)}
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10"><Link data-testid="print-incident-button" to={`/unfall/${id}/drucken?autoprint=1`}><Printer size={16} className="mr-2" />Drucken</Link></Button>
          <Button 
            variant="outline" 
            data-testid="edit-incident-button"
            onClick={() => navigate(`/unfall/${id}/edit`)}
            className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10 hover:border-yellow-500"
          >
            <Edit className="w-4 h-4 mr-2" />
            Bearbeiten
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                data-testid="delete-incident-button"
                className="border-red-500/30 text-red-500 hover:bg-red-500/10 hover:border-red-500"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-zinc-50">Unfallmeldung löschen?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">
                  Diese Aktion kann nicht rückgängig gemacht werden. Die Unfallmeldung wird dauerhaft gelöscht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  Abbrechen
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-500 text-white hover:bg-red-600"
                >
                  {deleting ? "Löschen..." : "Löschen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-3 mb-8">
        {incident.geschaeftsfuehrung_informieren && (
          <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-sm px-3 py-1.5 rounded-sm flex items-center gap-2" data-testid="badge-gf-informiert">
            <AlertTriangle className="w-4 h-4" />
            Benachrichtigung der Geschäftsführung angefordert
          </span>
        )}
        {incident.wegeunfall && (
          <span className="badge-warning text-sm px-3 py-1.5 rounded-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Wegeunfall
          </span>
        )}
        {incident.behandlung && (
          <span className="badge-warning text-sm px-3 py-1.5 rounded-sm flex items-center gap-2">
            <Check className="w-4 h-4" />
            Behandlung erfolgt
          </span>
        )}
        {incident.arbeit_eingestellt && (
          <span className="badge-danger text-sm px-3 py-1.5 rounded-sm flex items-center gap-2">
            <X className="w-4 h-4" />
            Arbeit eingestellt
          </span>
        )}
      </div>

      {/* Stored field labels and values stay readable after configuration changes. */}
      {incident.form_snapshot?.fields?.length ? <IncidentValues incident={incident} /> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Beschreibung */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Unfallbeschreibung
            </h2>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid="incident-description">
              {incident.beschreibung}
            </p>
          </div>

          {/* Ursache */}
          {incident.ursache && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Unfallursache
              </h2>
              <p className="text-zinc-300" data-testid="incident-ursache">{incident.ursache}</p>
            </div>
          )}

          {/* Bilder */}
          {incident.bilder && incident.bilder.length > 0 && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Dokumentation
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {incident.bilder.map((path, index) => (
                  <div key={index} className="aspect-square bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
                    {imageUrls[path] ? (
                      <img 
                        src={imageUrls[path]} 
                        alt={`Unfall Bild ${index + 1}`}
                        data-testid={`incident-image-${index}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-zinc-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Beteiligte Personen */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Beteiligte Personen
            </h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Verunfallter</dt>
                <dd className="text-zinc-300 font-medium" data-testid="detail-verunfallter">{incident.verunfallter_name || "-"}</dd>
              </div>
              {incident.zeugen && (
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Zeugen</dt>
                  <dd className="text-zinc-300" data-testid="detail-zeugen">{incident.zeugen}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Erstellt von</dt>
                <dd className="text-zinc-400 text-sm" data-testid="detail-erstellt-von">{incident.erstellt_von_name || "Unbekannt"}</dd>
              </div>
            </dl>
          </div>

          {/* Details */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Details
            </h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Abteilung</dt>
                <dd className="text-zinc-300" data-testid="detail-abteilung">{incident.abteilung}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Verletztes Körperteil</dt>
                <dd className="text-zinc-300" data-testid="detail-koerperteil">{incident.koerperteil}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Art der Verletzung</dt>
                <dd className="text-zinc-300" data-testid="detail-verletzungsart">{incident.verletzungsart}</dd>
              </div>
              <div>
                <dt className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Voraussichtliche Fehltage</dt>
                <dd className="text-2xl font-black text-yellow-500" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="detail-fehltage">
                  {incident.fehltage || 0}
                </dd>
              </div>
            </dl>
          </div>

          {/* Status Overview */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Status
            </h2>
            <ul className="space-y-3">
              <li className="flex items-center justify-between">
                <span className="text-zinc-400">Wegeunfall</span>
                {incident.wegeunfall ? (
                  <Check className="w-5 h-5 text-yellow-500" />
                ) : (
                  <X className="w-5 h-5 text-zinc-600" />
                )}
              </li>
              <li className="flex items-center justify-between">
                <span className="text-zinc-400">Behandlung</span>
                {incident.behandlung ? (
                  <Check className="w-5 h-5 text-yellow-500" />
                ) : (
                  <X className="w-5 h-5 text-zinc-600" />
                )}
              </li>
              <li className="flex items-center justify-between">
                <span className="text-zinc-400">Arbeit eingestellt</span>
                {incident.arbeit_eingestellt ? (
                  <Check className="w-5 h-5 text-red-500" />
                ) : (
                  <X className="w-5 h-5 text-zinc-600" />
                )}
              </li>
              {incident.meldungsart === "Arbeitsunfall" && (
                <li className="flex items-center justify-between pt-2 border-t border-zinc-800 mt-2">
                  <span className="text-red-400 font-medium">GF informiert</span>
                  {incident.geschaeftsfuehrung_informieren ? (
                    <Check className="w-5 h-5 text-red-500" data-testid="status-gf-check" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-600" data-testid="status-gf-x" />
                  )}
                </li>
              )}
            </ul>
          </div>

          {/* Timestamp */}
          <div className="text-xs text-zinc-500">
            Erstellt: {incident.created_at ? formatDate(incident.created_at) : 'Unbekannt'}
          </div>
        </div>
      </div>}
    </div>
  );
}
